import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import classNames from "classnames";
import styles from "./Field.module.scss";
import Calendar from "./Calendar";
import Icon from "../images/Icon";
import ModalDialogBox from "../windows/ModalDialogBox";
import VideoPlayer from '../players/VideoPlayer';
import { convertStringToDate } from '../../../dataTypes/timeManagment/utils';


// Durée pendant laquelle la coche verte remplace l'icône « copier ».
const COPIED_FEEDBACK_MS = 2000;

export interface FieldProps {
  type?:
    | "text"
    | "password"
    | "calendar"
    | "calendar_with_hour"
    | "url_or_file_image"
    | "url_or_file_video"
    | "url_or_file_audio"
    | "number"
    | "richText"
    | "color";
  placeholder?: string;
  value?: string | number;
  autocomplete?: string;
  readOnly?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string | number | File | Date, duration?: string) => void;
  onClick?:  (event: React.MouseEvent<HTMLInputElement>) => void;
  eyeVisible?: boolean;
  width?: string;
  height?: string;
  previewWidth?: string;
  glowColor?: string;
  numberFormat?: string;
  dateFormat?: string;
  baseURL?: string;
  extension?: string;
  showPreview?: boolean;
  decimalPlaces?: number;
  copyable?: boolean;
  // Longueur maximale de la saisie (champs texte) : le navigateur tronque frappe et collage.
  maxLength?: number;
mailSendable?: boolean;
onMailSend?:   () => void;
iconSquareColor?: string;
}

export const Field = ({
  type = "text",
  placeholder,
  value = "",
  autocomplete,
  readOnly,
  onChange,
  onClick,
  eyeVisible,
  width = "100%",
  height = "26px",
  previewWidth,
  glowColor = "var(--color-Field-Focus-normal)",
  numberFormat = "decimal:2",
  dateFormat = "dd/MM/yyyy",
  baseURL = "",
  extension = "",
  showPreview = true,
  decimalPlaces = 0,
  copyable = false,
maxLength,
mailSendable = false,
onMailSend,
iconSquareColor = "var(--color-Field-Border-normal)",
}: FieldProps) => {
  const fieldStyle = {
    "--glow-color": glowColor,
    "--icon-square-color": iconSquareColor,
    "--field-control-size": height,
  } as React.CSSProperties;

  const [eyeSlashed, setEyeSlashed] = useState(true);
  const [mediaSrc, setMediaSrc] = useState("");
  const [inputValue, setInputValue] = useState(value);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isBlob, setIsBlob] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const colorInputRef = useRef<HTMLInputElement>(null);

  // Function to remove unnecessary leading zeros
  const formatInputValue = (value: String) => {
    let strValue = value.toString();

    if (strValue.includes('.')) {
      const [integerPart, decimalPart] = strValue.split('.');
      const formattedIntegerPart = integerPart.replace(/^0+/, '') || '0';
      return `${formattedIntegerPart}.${decimalPart}`;
    } else {
      return strValue.replace(/^0+/, '') || '0';
    }
  };

  useEffect(() => {
    if (!hasFocus) {
      setInputValue(value);

      if (value && typeof value === "string" && !isBlob) {
        const fullPath = constructFullPath(value, baseURL, extension);

        if (type === "url_or_file_image" || type === "url_or_file_video" || type === "url_or_file_audio") {
          setMediaSrc(fullPath);
        }
      }

      if (type === "number") {
        const regex = /^-?\d*\.?\d*$/;
        const input2 = value !== '' ? value : '0';

        if (regex.test(String(input2))) {
          const parsedValue = Number(input2);
          const formattedValue = decimalPlaces !== undefined ? parsedValue.toFixed(decimalPlaces) : parsedValue;
          setInputValue(formattedValue);
        }
      }
    }
  }, [value, type, baseURL, extension, hasFocus]);

  useEffect(() => {
    // Autres effets si nécessaire
  }, [inputValue]);

  const toggleEye = () => setEyeSlashed((prev) => !prev);

  const handleChange = (event: ChangeEvent<HTMLInputElement>, isFile: boolean = false) => {
    const input = event.target.value;

    if (type === "number") {
      const regex = new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}$`);
      const input2 = input !== '' ? input : '0';

      if (regex.test(input2)) {
        const parsedValue = Number(input2);
        const formattedInputValue = formatInputValue(input2);
        setInputValue(formattedInputValue);
        if (onChange) onChange(event, parsedValue);
      }
    } else if (type === "calendar" || type === "calendar_with_hour") {
      const parsedValue = convertStringToDate(input, dateFormat);
      if (parsedValue !== null) {
        setInputValue(input);
        if (onChange) onChange(event, parsedValue as Date);
      }
    } else if (isFile && event.target.files) {
      const file = event.target.files[0];

      if (file) {
        if (file.size > 0) {
          setInputValue(file.name);
          const objectURL = URL.createObjectURL(file);
          setMediaSrc(objectURL);
          setIsBlob(true);

          if (type === "url_or_file_video" || type === "url_or_file_audio") {
            const mediaElement = document.createElement(type === "url_or_file_video" ? "video" : "audio");
            mediaElement.src = objectURL;
            mediaElement.onloadedmetadata = () => {
              const duration = mediaElement.duration;
              const formattedDuration = new Date(duration * 1000).toISOString().substr(11, 8);
              if (onChange) onChange(event, file, formattedDuration);
            };
          } else {
            if (onChange) onChange(event, file);
          }
        } else {
          console.error("File is empty");
        }
      }
    } else {
      if (!isBlob) {
        const fullPath = constructFullPath(input, baseURL, extension);
        setMediaSrc(fullPath);
      }

      setInputValue(input);
      if (onChange) onChange(event, input);
    }
  };

  const handleDateChange = (date: string) => {
    setInputValue(date);
    setShowCalendar(false);

    if (onChange) {
      const fakeEvent = {
        target: {
          value: date,
        },
      } as unknown as ChangeEvent<HTMLInputElement>;

      onChange(fakeEvent, date);
    }
  };

  const renderPreview = (): JSX.Element | null => {
    if (!mediaSrc || !showPreview) return null;

    const previewStyle = {
      width: previewWidth || "100%",
      height: "auto",
    };

    if (type === "url_or_file_image") {
      return <img src={mediaSrc} alt="Preview" className={classNames(styles.img)} style={previewStyle} />;
    }

    if (type === "url_or_file_video") {
      if (mediaSrc.startsWith('blob')) {
        return <video src={mediaSrc} controls className={classNames(styles.video)} style={previewStyle} />;
      } else {
        return <VideoPlayer url={`${mediaSrc}/index.m3u8`} className={classNames(styles.video)} previewStyle={previewStyle} />;
      }
    }

    if (type === "url_or_file_audio") {
      return <audio src={mediaSrc} controls />;
    }

    return null;
  };

  const fileInputId = useRef(`file-input-hidden-${Math.random().toString(36).substr(2, 9)}`);

  // Bouton « copier » : l'icône devient une coche verte le temps de COPIED_FEEDBACK_MS, pour
  // montrer que la copie est faite, puis revient à l'icône normale.
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current); }, []);

  const copyInputValue = () => {
    navigator.clipboard?.writeText(String(inputValue)).then(() => {
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    }).catch(() => { /* copie refusée par le navigateur : l'icône reste inchangée */ });
  };

  const renderCopyIcon = () => copied
    ? <Icon name="Check" color="#22c55e" hoverColor="#22c55e" width={18} height={18} />
    : <Icon name="copy"  color="var(--color-Field-Icon-normal)" hoverColor="var(--color-Field-Icon-hover-normal)" width={18} height={18} />;

  // Wrapper qui place une icône dans un carré coloré (couleur par défaut = contour du champ)
  const IconSquare = ({
    children,
    onClick,
  }: {
    children: React.ReactNode;
    onClick?: () => void;
  }) => (
    <div className={classNames(styles.icon_square)} onClick={onClick}>
      {children}
    </div>
  );

const renderInput = (): JSX.Element => {
    const textInputProps = {
      className:    classNames(styles.field_input),
      placeholder,
      value:        inputValue,
      readOnly,
      autoComplete: autocomplete === "on" ? "on" : "new-password",
      maxLength,
      onChange:     (e: ChangeEvent<HTMLInputElement>) => handleChange(e),
      onFocus:      () => setHasFocus(true),
      onBlur:       () => setHasFocus(false),
      onClick,
    };

    const fileInputProps = {
      className: classNames(styles.file_input_hidden),
      accept:    type === "url_or_file_image" ? "image/*" : type === "url_or_file_video" ? "video/*" : "audio/*",
      onChange:  (e: ChangeEvent<HTMLInputElement>) => handleChange(e, true),
      id:        fileInputId.current,
    };

    switch (type) {
      case "password":
        return (
          <div className={classNames(styles.password_field, styles.field_with_icon)}>
            <input {...textInputProps} type={eyeSlashed ? "password" : "text"} />
            {eyeVisible && (
              <IconSquare onClick={toggleEye}>
                <Icon
                  name={eyeSlashed ? "slashedEye" : "eye"}
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
            )}
          </div>
        );

      case "calendar":
      case "calendar_with_hour":
        return (
          <div className={classNames(styles.field_with_icon, styles.field_with_calendar)}>
            <input {...textInputProps} type="text" />
            <IconSquare onClick={() => setShowCalendar(!showCalendar)}>
              <Icon
                name="calendar"
                color="var(--color-Field-Icon-normal)"
                hoverColor="var(--color-Field-Icon-hover-normal)"
                width={18}
                height={18}
              />
            </IconSquare>
            {copyable && (
              <IconSquare onClick={copyInputValue}>
                {renderCopyIcon()}
              </IconSquare>
            )}
            {mailSendable && onMailSend && (
              <IconSquare onClick={() => onMailSend!()}>
                <Icon
                  name="envelope"
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
            )}
          </div>
        );

      case "url_or_file_image":
      case "url_or_file_video":
      case "url_or_file_audio":
        return (
          <div className={classNames(styles.file_input_wrapper)}>
            <div className={classNames(styles.field_with_icon)}>
              <input {...fileInputProps} type="file" />
              <input
                type="text"
                className={classNames(styles.field_input)}
                placeholder={placeholder}
                value={typeof inputValue === "string" ? inputValue : ""}
                onFocus={() => setHasFocus(true)}
                onBlur={()  => setHasFocus(false)}
                onClick={onClick}
              />
              <IconSquare
                onClick={() => {
                  const fileInput = document.getElementById(fileInputId.current);
                  if (fileInput) fileInput.click();
                }}
              >
                <Icon
                  name="upload"
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
              {copyable && (
                <IconSquare onClick={copyInputValue}>
                {renderCopyIcon()}
              </IconSquare>
              )}
              {mailSendable && onMailSend && (
                <IconSquare onClick={() => onMailSend!()}>
                  <Icon
                    name="envelope"
                    color="var(--color-Field-Icon-normal)"
                    hoverColor="var(--color-Field-Icon-hover-normal)"
                    width={18}
                    height={18}
                  />
                </IconSquare>
              )}
            </div>
            {renderPreview()}
          </div>
        );

      case "number":
        return (
          <div className={classNames(styles.field_with_icon)}>
            <input
              {...textInputProps}
              type="text"
              placeholder={placeholder || "Enter a number"}
              value={inputValue === "" ? 0 : inputValue}
            />
            {copyable && (
              <IconSquare onClick={copyInputValue}>
                {renderCopyIcon()}
              </IconSquare>
            )}
            {mailSendable && onMailSend && (
              <IconSquare onClick={() => onMailSend!()}>
                <Icon
                  name="envelope"
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
            )}
          </div>
        );

      case "color":
        return (
          <div className={classNames(styles.field_with_icon, styles.color_picker)}>
            <input
              {...textInputProps}
              type="text"
              placeholder={placeholder || "var(--color-Field-FG-normal)"}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
            />
            <input
              ref={colorInputRef}
              type="color"
              value={inputValue as string}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
              style={{ opacity: 0, position: 'absolute', pointerEvents: 'none' }}
            />
            <button
              className={classNames(styles.color_preview_button)}
              style={{ backgroundColor: inputValue as string }}
              onClick={() => {
                if (colorInputRef.current) colorInputRef.current.click();
              }}
            />
            {copyable && (
              <IconSquare onClick={copyInputValue}>
                {renderCopyIcon()}
              </IconSquare>
            )}
            {mailSendable && onMailSend && (
              <IconSquare onClick={() => onMailSend!()}>
                <Icon
                  name="envelope"
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
            )}
          </div>
        );

      default:
        return (
          <div className={classNames(styles.field_with_icon)}>
            <input {...textInputProps} type="text" />
            {copyable && (
              <IconSquare onClick={copyInputValue}>
                {renderCopyIcon()}
              </IconSquare>
            )}
            {mailSendable && onMailSend && (
              <IconSquare onClick={() => onMailSend!()}>
                <Icon
                  name="envelope"
                  color="var(--color-Field-Icon-normal)"
                  hoverColor="var(--color-Field-Icon-hover-normal)"
                  width={18}
                  height={18}
                />
              </IconSquare>
            )}
          </div>
        );
    }
  };


  const constructFullPath = (value: string, baseURL: string, extension: string) => {
    if (value.startsWith("http://")) {
      return value;
    }

    let fullPath = value;
    if (!value.startsWith("http") && !value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      fullPath += `${extension || getDefaultExtension(type)}`;
    }

    return fullPath;
  };

  const getDefaultExtension = (type: string) => {
    switch (type) {
      case "url_or_file_image": return "webp";
      case "url_or_file_video": return "";
      case "url_or_file_audio": return "aac";
      default:                  return "";
    }
  };

  return (
    <div
      className={classNames(styles.field)}
      style={{ width, height, cursor: onClick ? 'pointer' : undefined, ...fieldStyle }}
    >
      {renderInput()}
      <ModalDialogBox isOpen={showCalendar} onClose={() => setShowCalendar(false)}>
        <Calendar onDateClick={handleDateChange} showHourPanel={type === "calendar_with_hour"} />
      </ModalDialogBox>
    </div>
  );
};

export default Field;